import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Router, Switch, Redirect } from "react-router-dom";
import { ROUTES } from "../constants/routes";
import { ModalComponent } from "../pages/components/modal";
import { NoAuthPages } from "../pages/NoAuthPages";
import { Navbar } from "../pages/components/Navbar";
import { AuthPages } from "../pages/AuthPages";
import { SchoolAuthPages } from "../pages/SchoolAuthPages";
import { PageNotFound } from "../pages/PageNotFound";
import { userLogout, setDeviceAndLocationPayload } from "../store/auth/auth.slice";
import {
  getIsAuthenticated,
  getUserInfo,
  isSchoolUserType,
  isStudentUserType,
  getDeviceAndLocation,
  getSelectedEventsDetails
} from "../store/auth/selectors";
import { history } from "./history";
import { SessionTimeout } from "../pages/components/SessionTimeout";
import { StudentLoginPage } from "../pages/StudentLoginPage";
import { StudentSignUpWidget } from "../pages/StudentSignUpWidget";
import { SchoolSignUpWidget } from "../pages/components/SchoolSignUpWidget";
import { RequestCodeWidget } from "../pages/auth-widget/RequestCodeWidget";
import { StudentOTPLoginPage } from "../pages/StudentOTPLoginPage";
import { TAndC } from "../pages/T&C";
import { PAndP } from "../pages/PP";
import { ExamPageNav } from "../pages/components/examPageNav";
import { TNCModal } from "../pages/components/modal/t&cModal";
import { PNPModal } from "../pages/components/modal/p&pModal";
import DeepLink from "../pages/DeepLink";
import AuthErrorBoundary from "../pages/components/AuthErrorBoundary";
import AlertBar from "../pages/components/AlertBar";
import ServiceIndex from "../pages/components/services/ServiceIndex";
import IdlLandingPage from "../pages/IDL/IdlLandingPage";
import { IDlNavBar } from "../pages/components/IdlNavBar";
import { CareerCounsellingWidget } from "../pages/CareerCounsellingWidget";
import Redirection from "../pages/redirection/Redirection";
import { RecaptchaProvider } from "../hooks/useRecaptcha";
import { ExamConfigHelper } from "../constants/examConfig";
import { getDeviceAndLocationPayload } from "../utils/getDeviceDetails.utils";
import dashboardLoaderGif from "../assets/gif/ReportsLoaderGif.gif";
import {
  clearPostSignupOverlay,
  getPostSignupOverlay,
  POST_SIGNUP_OVERLAY_EVENT
} from "../utils/postSignupOverlay.utils";
import { CONFIG_VARS } from "../constants/configuration";
import { GeniusRoutes } from "../genius/integration/geniusRouter";
import { isGeniusRoute } from "../genius/integration/geniusRouteConstants";

const POST_SIGNUP_OVERLAY_DELAY_MS = 6000;

// Constants for landing source tracking with 24-hour expiry
const LANDING_SOURCE_KEY = "landing_source";
const LANDING_URL_KEY = "landing_url";
const LANDING_TIMESTAMP_KEY = "landing_timestamp";
const LANDING_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

const MainRouter = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(getIsAuthenticated);
  const isStudent = useSelector(isStudentUserType);
  const isSchool = useSelector(isSchoolUserType);
  const userInfo = useSelector(getUserInfo);
  const deviceAndLocation = useSelector(getDeviceAndLocation);
  const selectedEventsDetails = useSelector(getSelectedEventsDetails);
  const [isExamPage, setIsExamPage] = useState(false);
  const [showTAndC, setShowTAndC] = useState(false);
  const [showPAndP, setShowPAndP] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(() => history.location.pathname);
  const [postSignupOverlay, setPostSignupOverlay] = useState(getPostSignupOverlay());

  const onGeniusRoute = isGeniusRoute(currentLocation);

  const shouldShowTopBanners = useMemo(() => {
    if (onGeniusRoute) {
      return false;
    }
    if (currentLocation === "/idl" || currentLocation.startsWith("/idl/")) {
      return false;
    }
    if (currentLocation.includes("/reports/") || currentLocation.includes("/exam-page")) {
      const examTitle = selectedEventsDetails?.title;
      if (examTitle) {
        return ExamConfigHelper.shouldShowTopBanners(examTitle);
      }
    }

    return true;
  }, [currentLocation, selectedEventsDetails, onGeniusRoute]);

  const currentTheme = useMemo(() => {
    if (onGeniusRoute) {
      return "genius";
    }
    if (isAuthenticated && isStudent && isExamPage) {
      return "exam";
    } else if (history.location.pathname === "/idl") {
      return "idl";
    } else if ((isAuthenticated && isStudent) || !isAuthenticated) {
      return "dark";
    } else {
      return "light";
    }
  }, [isAuthenticated, isStudent, isExamPage, onGeniusRoute]);

  const checkAndSetDeviceLocation = async () => {
    const needsRefresh =
      !deviceAndLocation ||
      deviceAndLocation.city === "N/A" ||
      deviceAndLocation.city === null ||
      deviceAndLocation.state === "N/A" ||
      deviceAndLocation.state === null ||
      deviceAndLocation.pincode === "N/A" ||
      deviceAndLocation.pincode === null ||
      deviceAndLocation.webType === "N/A" ||
      deviceAndLocation.webType === null ||
      deviceAndLocation.ip_address === "N/A" ||
      deviceAndLocation.ip_address === null ||
      deviceAndLocation.latitude === null ||
      deviceAndLocation.longitude === null;

    if (needsRefresh) {
      try {
        const payload = await getDeviceAndLocationPayload();
        dispatch(setDeviceAndLocationPayload(payload));
      } catch (error) {
        console.error("Error getting device and location data:", error);
      }
    }
  };

  useEffect(() => {
    document.body.classList.remove("theme-exam", "theme-idl", "theme-dark", "theme-light", "theme-genius");
    document.body.classList.add(`theme-${currentTheme}`);

    return () => {
      document.body.classList.remove(`theme-${currentTheme}`);
    };
  }, [currentTheme]);

  useEffect(() => {
    checkAndSetDeviceLocation();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsExamPage(false);
      if (getPostSignupOverlay()) {
        clearPostSignupOverlay();
      }
    }
  }, [isExamPage, isAuthenticated]);

  useEffect(() => {
    const handleOverlayChange = () => {
      setPostSignupOverlay(getPostSignupOverlay());
    };

    window.addEventListener(POST_SIGNUP_OVERLAY_EVENT, handleOverlayChange);
    return () => {
      window.removeEventListener(POST_SIGNUP_OVERLAY_EVENT, handleOverlayChange);
    };
  }, []);

  const waitForEventsBeforeClearOverlay = CONFIG_VARS?.featureFlags?.deeplinkSignupOverlayWaitForEvents ?? false;

  useEffect(() => {
    if (!waitForEventsBeforeClearOverlay && postSignupOverlay) {
      const timer = setTimeout(() => {
        clearPostSignupOverlay();
      }, POST_SIGNUP_OVERLAY_DELAY_MS);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [postSignupOverlay, waitForEventsBeforeClearOverlay]);

  useEffect(() => {
    const unlisten = history.listen((location) => {
      setCurrentLocation(location.pathname);
    });
    return () => {
      unlisten();
    };
  }, []);

  const isLoginFlowPath = (path) => {
    if (!path) return false;
    if (path.startsWith("/deeplink")) return true;
    if (path === "/") return !isAuthenticated;
    return path.startsWith(ROUTES.STUDENT) || path.startsWith(ROUTES.OTPLOGIN);
  };

  // Store URL for deeplink/login flow with 24-hour expiry
  useEffect(() => {
    if (isLoginFlowPath(currentLocation)) {
      const currentUrl = window.location.href;
      const isDeeplink = currentLocation.startsWith("/deeplink");
      const newSource = isDeeplink ? "deeplink" : "organic";
      const now = Date.now();

      // Check existing stored data
      const storedTimestamp = sessionStorage.getItem(LANDING_TIMESTAMP_KEY);
      const storedSource = sessionStorage.getItem(LANDING_SOURCE_KEY);

      let shouldStore = true;

      if (storedTimestamp) {
        const timestamp = parseInt(storedTimestamp, 10);
        const isExpired = now - timestamp > LANDING_EXPIRY_MS;

        // Keep existing if not expired and source is the same
        if (!isExpired && storedSource === newSource) {
          shouldStore = false;
        }
      }

      if (shouldStore) {
        // Store original URL (existing functionality)
        sessionStorage.setItem("url", currentUrl);
        // Store enhanced tracking data
        sessionStorage.setItem(LANDING_SOURCE_KEY, newSource);
        sessionStorage.setItem(LANDING_URL_KEY, currentUrl);
        sessionStorage.setItem(LANDING_TIMESTAMP_KEY, now.toString());
        console.log("[Landing] Stored with expiry:", { source: newSource, url: currentUrl, timestamp: now });
      } else {
        console.log("[Landing] Using existing (not expired):", { source: storedSource });
      }
    }
  }, [currentLocation, isAuthenticated]);

  // Function to get landing source (can be used anywhere)
  const getLandingSource = () => {
    const storedTimestamp = sessionStorage.getItem(LANDING_TIMESTAMP_KEY);
    const storedSource = sessionStorage.getItem(LANDING_SOURCE_KEY);

    if (storedTimestamp) {
      const timestamp = parseInt(storedTimestamp, 10);
      const isExpired = Date.now() - timestamp > LANDING_EXPIRY_MS;

      if (isExpired) {
        // Clear expired data
        sessionStorage.removeItem(LANDING_SOURCE_KEY);
        sessionStorage.removeItem(LANDING_URL_KEY);
        sessionStorage.removeItem(LANDING_TIMESTAMP_KEY);
        return "organic";
      }
    }

    return storedSource || "organic";
  };

  useEffect(() => {
    const currentPath = history.location.pathname;
    const redirectToPath = () => {
      if (currentPath === ROUTES.REDIRECTION) {
        return null;
      }

      switch (true) {
        case !isAuthenticated:
          if (currentPath === ROUTES.STUDENT) {
            return ROUTES.STUDENT;
          }
          break;
        case isStudent:
          if (userInfo?.is_active) {
            if (
              currentPath === ROUTES.STUDENT_CREATE_ACCOUNT ||
              currentPath === ROUTES.OTPLOGIN ||
              currentPath.startsWith("/deeplink/register")
            ) {
              return ROUTES.DASHBOARD;
            } else {
              return currentPath;
            }
          } else {
            return ROUTES.STUDENT_ONBOARDING;
          }
        case isSchool:
          if (userInfo?.is_active) {
            dispatch(userLogout());
            return ROUTES.HOME;
          } else {
            return ROUTES.SCHOOL_ONBOARDING;
          }
        default:
          return null;
      }
    };

    const pathToRedirect = redirectToPath();
    if (pathToRedirect) {
      history.push(pathToRedirect);
    }
  }, [isAuthenticated]);

  const handleCloseModal = () => {
    setShowTAndC(false);
    setShowPAndP(false);
  };

  return (
    <AuthErrorBoundary>
      <div className={`theme-${currentTheme} body-wrapbox`} style={{ position: "relative" }}>
        {showTAndC && <TNCModal handleClose={handleCloseModal} />}
        {showPAndP && <PNPModal handleClose={handleCloseModal} />}
        <Router history={history}>
          {shouldShowTopBanners && <AlertBar currentPath={currentLocation} />}

          {!onGeniusRoute &&
            (history.location.pathname === "/idl" ? (
              <IDlNavBar />
            ) : isExamPage ? (
              <ExamPageNav currentLocation={currentLocation} />
            ) : (
              <Navbar isAuthenticated={isAuthenticated} />
            ))}
          <SessionTimeout />
          {onGeniusRoute ? (
            <GeniusRoutes />
          ) : (
            <Switch>
              <Route path={ROUTES.IDL} component={IdlLandingPage} />
              <Route
                path={"/deeplink/:path"}
                render={() => (
                  <RecaptchaProvider>
                    <DeepLink
                      toggleTAndC={() => setShowTAndC(!showTAndC)}
                      togglePAndP={() => setShowPAndP(!showPAndP)}
                    />
                  </RecaptchaProvider>
                )}
              />
              <Route
                path={ROUTES.STUDENT_CREATE_ACCOUNT}
                render={() => (
                  <RecaptchaProvider>
                    <StudentSignUpWidget
                      toggleTAndC={() => setShowTAndC(!showTAndC)}
                      togglePAndP={() => setShowPAndP(!showPAndP)}
                    />
                  </RecaptchaProvider>
                )}
              />
              <Route path={ROUTES.TANDC} component={TAndC} />
              <Route path={ROUTES.PANDP} component={PAndP} />
              <Route
                path={ROUTES.SCHOOL_CREATE_ACCOUNT}
                render={() => (
                  <RecaptchaProvider>
                    <SchoolSignUpWidget
                      toggleTAndC={() => setShowTAndC(!showTAndC)}
                      togglePAndP={() => setShowPAndP(!showPAndP)}
                    />
                  </RecaptchaProvider>
                )}
              />
              <Route
                path={ROUTES.OTPLOGIN}
                render={() => (
                  <RecaptchaProvider>
                    <StudentOTPLoginPage
                      toggleTAndC={() => setShowTAndC(!showTAndC)}
                      togglePAndP={() => setShowPAndP(!showPAndP)}
                    />
                  </RecaptchaProvider>
                )}
              />
              <Route
                path={ROUTES.CAREER_COUNSELING}
                render={() => (
                  <CareerCounsellingWidget
                    toggleTAndC={() => setShowTAndC(!showTAndC)}
                    togglePAndP={() => setShowPAndP(!showPAndP)}
                  />
                )}
              />
              <Route
                path={ROUTES.REQUEST_CODE}
                render={() => (
                  <RecaptchaProvider>
                    <RequestCodeWidget />
                  </RecaptchaProvider>
                )}
              />
              <Route path={ROUTES.PAGE_NOT_FOUND} component={PageNotFound} />
              <Route path={ROUTES.REDIRECTION} component={Redirection} />
              {isAuthenticated ? (
                <Route
                  path={"/"}
                  render={() =>
                    isSchool ? <SchoolAuthPages /> : <AuthPages isExamPage={isExamPage} setIsExamPage={setIsExamPage} />
                  }
                />
              ) : (
                <Route
                  path={"/"}
                  render={() => (
                    <RecaptchaProvider>
                      <StudentLoginPage
                        toggleTAndC={() => setShowTAndC(!showTAndC)}
                        togglePAndP={() => setShowPAndP(!showPAndP)}
                      />
                    </RecaptchaProvider>
                  )}
                />
              )}
              <Route path="*">
                <Redirect to={ROUTES.PAGE_NOT_FOUND} />
              </Route>
            </Switch>
          )}
        </Router>
        {postSignupOverlay && (
          <div className="modal-overlayll" style={{ zIndex: 1001 }}>
            <div className="modalll" style={{ textAlign: "center", maxWidth: "420px" }}>
              <img src={dashboardLoaderGif} alt="Loading Dashboard..." style={{ width: "240px", height: "240px" }} />
              <p className="text-center mt-3 text-white">
                {postSignupOverlay.message || "Please wait while we are preparing your Dashboard"}
              </p>
            </div>
          </div>
        )}
        <ModalComponent />
        <ServiceIndex currentPath={currentLocation} />
      </div>
    </AuthErrorBoundary>
  );
};

export { MainRouter };
